import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Terminal,
  Lock,
  Wifi,
  WifiOff,
  Trash2,
  Download,
  Pause,
  Play,
  RefreshCw,
  LogOut,
  History,
  Clock,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import {
  verifyLogsPassword,
  getLogsWebSocketUrl,
  LogsWebSocketMessage,
  listSessions,
  getSessionLogs,
  SessionInfo
} from '@/services/logs';

type ViewState = 'password' | 'connecting' | 'streaming' | 'error' | 'loading_session';

export default function LogViewer() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const [viewState, setViewState] = useState<ViewState>('password');
  const [password, setPassword] = useState('');
  const [logsToken, setLogsToken] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);

  // Session-based state
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  const handleLogout = async () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    await logout();
    navigate('/login');
  };

  const wsRef = useRef<WebSocket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const pausedLogsRef = useRef<string[]>([]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (isAutoScroll && logsEndRef.current && !isPaused) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isAutoScroll, isPaused]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectWebSocket = useCallback((token: string) => {
    try {
      const wsUrl = getLogsWebSocketUrl(token);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Log stream connected');
        setViewState('streaming');
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data: LogsWebSocketMessage = JSON.parse(event.data);

          switch (data.type) {
            case 'connected':
              console.log('Log stream ready:', data.message);
              break;
            case 'log':
              if (data.line !== undefined) {
                if (isPaused) {
                  pausedLogsRef.current.push(data.line);
                } else {
                  setLogs(prev => [...prev, data.line!]);
                }
              }
              break;
            case 'history_end':
              console.log('Historical logs loaded:', data.message);
              break;
            case 'error':
              setError(data.message || 'Stream error');
              break;
            case 'pong':
              // Ping response, ignore
              break;
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('Connection error');
        setViewState('error');
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        if (event.code !== 1000) {
          setError(`Connection closed: ${event.reason || 'Unknown reason'}`);
          setViewState('error');
        }
      };

      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      // Cleanup ping interval when connection closes
      ws.addEventListener('close', () => clearInterval(pingInterval));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setViewState('error');
    }
  }, [isPaused]);

  const fetchSessions = useCallback(async (token: string) => {
    setIsLoadingSessions(true);
    try {
      const response = await listSessions(token);
      setSessions(response.sessions);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  const loadSessionLogs = useCallback(async (sessionId: string) => {
    if (!logsToken) return;

    // Close WebSocket if open
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setSelectedSessionId(sessionId);
    setIsDrawerOpen(false);
    setLogs([]);
    setViewState('loading_session');

    try {
      const response = await getSessionLogs(logsToken, sessionId);
      setLogs(response.lines);
      setViewState('streaming');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session logs');
      setViewState('error');
    }
  }, [logsToken]);

  const switchToLive = useCallback(() => {
    if (!logsToken) return;
    setSelectedSessionId(null);
    setLogs([]);
    setIsDrawerOpen(false);
    setViewState('connecting');
    connectWebSocket(logsToken);
  }, [logsToken, connectWebSocket]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await verifyLogsPassword(password);

      if (response.success && response.token) {
        setLogsToken(response.token);
        setViewState('connecting');
        connectWebSocket(response.token);
        // Fetch sessions in the background
        fetchSessions(response.token);
      } else {
        setError(response.message || 'Invalid password');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify password');
    }
  };

  const handlePauseResume = () => {
    if (isPaused) {
      // Resume - add paused logs to display
      setLogs(prev => [...prev, ...pausedLogsRef.current]);
      pausedLogsRef.current = [];
    }
    setIsPaused(!isPaused);
  };

  const handleClear = () => {
    setLogs([]);
    pausedLogsRef.current = [];
  };

  const handleDownload = () => {
    const content = logs.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `server-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReconnect = () => {
    if (logsToken) {
      setViewState('connecting');
      setError(null);
      connectWebSocket(logsToken);
    } else {
      setViewState('password');
    }
  };

  // Password entry view
  if (viewState === 'password') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Log Viewer Access</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Enter the admin password to view server logs
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
              <Button type="submit" className="w-full" disabled={!password}>
                Access Logs
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connecting view
  if (viewState === 'connecting') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Connecting to log stream...</p>
        </div>
      </div>
    );
  }

  // Loading session view
  if (viewState === 'loading_session') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading session logs...</p>
        </div>
      </div>
    );
  }

  // Error view
  if (viewState === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <WifiOff className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle>Connection Error</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {error || 'Failed to connect to log stream'}
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={handleReconnect} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reconnect
            </Button>
            <Button
              variant="outline"
              onClick={() => setViewState('password')}
              className="w-full"
            >
              Enter Password Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLive = selectedSessionId === null;
  const selectedSession = sessions.find(s => s.session_id === selectedSessionId);

  // Format session ID for display (show first 8 chars)
  const formatSessionId = (id: string) => id.slice(0, 8);

  // Streaming view
  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <Terminal className="w-5 h-5 text-primary" />
          <h1 className="font-semibold">Server Logs</h1>
          {isLive ? (
            <Badge variant="outline" className="gap-1">
              <Wifi className="w-3 h-3 text-green-500" />
              Live
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <Clock className="w-3 h-3" />
              Session {formatSessionId(selectedSessionId!)}
            </Badge>
          )}
          {isPaused && isLive && (
            <Badge variant="secondary">
              Paused ({pausedLogsRef.current.length} buffered)
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* History button with drawer */}
          <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" onClick={() => fetchSessions(logsToken!)}>
                <History className="w-4 h-4 mr-1" />
                Sessions
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[380px]">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Chat Sessions
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-2">
                {isLoadingSessions ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : sessions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No sessions found
                  </p>
                ) : (
                  <ScrollArea className="h-[calc(100vh-120px)]">
                    <div className="space-y-1 pr-4">
                      {/* Live option */}
                      <button
                        onClick={switchToLive}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          isLive
                            ? 'bg-primary/10 border-primary'
                            : 'hover:bg-muted border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Wifi className="w-4 h-4 text-green-500" />
                            <span className="font-medium text-sm">Live Stream</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Current session (streaming)
                        </p>
                      </button>

                      {/* Session list */}
                      {sessions.map((session, index) => (
                        <button
                          key={session.session_id}
                          onClick={() => loadSessionLogs(session.session_id)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedSessionId === session.session_id
                              ? 'bg-primary/10 border-primary'
                              : 'hover:bg-muted border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                Session {formatSessionId(session.session_id)}
                              </span>
                              {session.is_current && (
                                <Badge variant="outline" className="text-xs py-0">
                                  Latest
                                </Badge>
                              )}
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                          {session.started_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {session.started_at}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Back to Live button (only when viewing session) */}
          {!isLive && (
            <Button
              variant="default"
              size="sm"
              onClick={switchToLive}
            >
              <Wifi className="w-4 h-4 mr-1" />
              Back to Live
            </Button>
          )}

          {/* Pause/Resume (only for live) */}
          {isLive && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePauseResume}
            >
              {isPaused ? (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4 mr-1" />
                  Pause
                </>
              )}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={logs.length === 0}
          >
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-1" />
            Logout
          </Button>
        </div>
      </div>

      {/* Log content */}
      <ScrollArea
        className="flex-1 p-4"
        ref={scrollRef}
      >
        <div className="font-mono text-sm space-y-0.5 text-left">
          {logs.length === 0 ? (
            <p className="text-muted-foreground py-8 text-left">
              {isLive ? 'Waiting for logs...' : 'No logs for this session'}
            </p>
          ) : (
            <>
              {logs.map((line, index) => (
                <div
                  key={index}
                  className={`py-0.5 px-2 rounded text-left ${
                    line.includes('ERROR') || line.includes('❌')
                      ? 'bg-destructive/10 text-destructive'
                      : line.includes('WARNING') || line.includes('⚠️')
                      ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                      : line.includes('✅')
                      ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {line}
                </div>
              ))}
              <div ref={logsEndRef} />
            </>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t px-4 py-2 text-xs text-muted-foreground bg-card flex justify-between">
        <span>{logs.length} lines</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isAutoScroll}
            onChange={(e) => setIsAutoScroll(e.target.checked)}
            className="rounded"
          />
          Auto-scroll
        </label>
      </div>
    </div>
  );
}
